import AccountLayout from '@components/common/AccountLayout'
import { useUser } from '@corratech/pylot-auth-manager'
import { useCountries } from '@components/alfakher/pylot-utils/src/hooks/useCountries'
import type { GetStaticProps, GetStaticPropsContext } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import {
    AccountHeader,
    AccountSubHeader
} from '@corratech/pylot-account-header'
import { InformationCard } from '@components/alfakher/pylot-account-information'
import { NewsletterCard } from '@components/alfakher/pylot-account-newsletter'
import { DefaultAddress } from '@components/alfakher/pylot-address-book/src/DefaultAddress/DefaultAddress'
import { OrderList } from '@corratech/pylot-orders'
import { useUI } from '@corratech/pylot-ui/context'
import s from '@pagestyles/Account.module.scss'
import cn from 'classnames'

export const getStaticProps: GetStaticProps = async ({
    locale
}: GetStaticPropsContext) => {
    return {
        props: {
            ...(await serverSideTranslations(locale!, ['common', 'account']))
        },
        revalidate: 14400
    }
}

export default function Account(): JSX.Element {
    const { loading, customer, addresses, isSubscribed } = useUser()
    const { countryNameLocale } = useCountries()

    const { LoadingIndicator } = useUI()
    const { t } = useTranslation(['account'])

    if (loading) {
        return (
            <div className="mx-auto max-w-xs">
                <LoadingIndicator />
            </div>
        )
    }

    return (
        <div className={s['account-container']}>
            <AccountHeader title={t('account|My Account')} />
            <div className={s['account-block']}>
                <AccountSubHeader
                    subHeading={t('account|Account Information')}
                />
                <div className={s['account-block-grid']}>
                    <InformationCard customer={customer!} />
                    <NewsletterCard isSubscribed={!!isSubscribed} />
                </div>
            </div>
            <div className={cn('manage-address', s['account-block'])}>
                <AccountSubHeader
                    link="/account/address"
                    linkText={t('account|Manage Addresses')}
                    subHeading={t('account|Address Book')}
                />
                <DefaultAddress
                    addresses={addresses}
                    countryNameLocale={countryNameLocale}
                />
            </div>
            <div className={cn('manage-orders', s['account-block'])}>
                <AccountSubHeader
                    link="/account/orders"
                    linkText={t('account|View All')}
                    subHeading={t('account|Recent Orders')}
                />
                <OrderList pageSize={5} currentPage={1} />
            </div>
        </div>
    )
}

Account.Protected = true
Account.Layout = AccountLayout
